import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { apiClient, readError, BACKEND_URL } from "@/lib/api";
import { fmtEUR, StatusBadge } from "@/components/StatusBadge";
import {
  Users,
  Briefcase,
  ShieldCheck,
  Banknote,
  Crown,
  Layers,
  Wallet,
  Building2,
  Save,
  Trash2,
  Star,
  FileText,
  Download,
  Receipt,
  ScrollText,
  Mail,
  Search,
  X as XIcon,
  Send,
  Eye,
  Phone,
  MapPin,
  Home,
  Sparkles,
  Scale,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

const DEMANDEUR_TYPE_LABEL = {
  particulier: "Particulier",
  architecte: "Architecte",
  promoteur_immobilier: "Promoteur immo.",
  cmi: "Constructeur (CMI)",
  syndic: "Syndic",
  bailleur: "Bailleur",
  entreprise_btp: "Entreprise BTP",
  collectivite: "Collectivité",
  maitre_oeuvre: "Maître d'œuvre",
  investisseur: "Investisseur",
};
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [missions, setMissions] = useState([]);
  const [txs, setTxs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [vat, setVat] = useState(null);
  const [settings, setSettings] = useState({});
  const [rgpd, setRgpd] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState(null);
  const [expandedArtisan, setExpandedArtisan] = useState(null);
  const [demandeursData, setDemandeursData] = useState(null);
  const [demandeurFilter, setDemandeurFilter] = useState("all");
  const [demandeurSearch, setDemandeurSearch] = useState("");
  const [openedDemandeur, setOpenedDemandeur] = useState(null); // full detail
  const [contactTarget, setContactTarget] = useState(null); // {id,name,email}
  const [contactForm, setContactForm] = useState({ subject: "", message: "" });
  const [contactSending, setContactSending] = useState(false);
  const [trialStats, setTrialStats] = useState(null);
  const [sweepingTrials, setSweepingTrials] = useState(false);
  const [disputes, setDisputes] = useState(null);
  const [prospects, setProspects] = useState(null);
  const [proposalDispute, setProposalDispute] = useState(null); // dispute being proposed on
  const [proposalForm, setProposalForm] = useState({ proposal: "", demandeur_refund_pct: 50 });
  const [proposalSending, setProposalSending] = useState(false);
  const [tab, setTab] = useState("overview");
  const [savingSettings, setSavingSettings] = useState(false);

  const reload = async () => {
    const [s, u, m, t, set, inv, v] = await Promise.all([
      apiClient.get("/admin/stats"),
      apiClient.get("/admin/users"),
      apiClient.get("/admin/missions"),
      apiClient.get("/admin/transactions"),
      apiClient.get("/admin/settings"),
      apiClient.get("/admin/invoices"),
      apiClient.get("/admin/vat-summary"),
    ]);
    setStats(s.data);
    setUsers(u.data.users || []);
    setMissions(m.data.missions || []);
    setTxs(t.data.transactions || []);
    setSettings(set.data.settings || {});
    setInvoices(inv.data.invoices || []);
    setVat(v.data);
  };

  useEffect(() => {
    reload().catch((e) => toast.error(readError(e)));
  }, []);

  useEffect(() => {
    if (tab === "rgpd" && !rgpd) {
      apiClient.get("/admin/rgpd-audit").then(({ data }) => setRgpd(data)).catch((e) => toast.error(readError(e)));
    }
    if (tab === "payouts") {
      apiClient.get("/admin/pending-payouts").then(({ data }) => setPendingPayouts(data)).catch((e) => toast.error(readError(e)));
    }
    if (tab === "demandeurs" && !demandeursData) {
      apiClient.get("/admin/demandeurs").then(({ data }) => setDemandeursData(data)).catch((e) => toast.error(readError(e)));
    }
    if (tab === "trials") {
      apiClient.get("/admin/trial-nurture-stats").then(({ data }) => setTrialStats(data)).catch((e) => toast.error(readError(e)));
    }
    if (tab === "disputes") {
      apiClient.get("/admin/disputes").then(({ data }) => setDisputes(data)).catch((e) => toast.error(readError(e)));
    }
    if (tab === "prospects" && !prospects) {
      apiClient.get("/admin/city-prospects").then(({ data }) => setProspects(data)).catch((e) => toast.error(readError(e)));
    }
  }, [tab, rgpd, demandeursData]);

  // ── Live polling for new disputes → browser push notifications ──
  const lastDisputeSeenRef = useRef(null);
  const notifPermissionRef = useRef(null);
  const [notifCounts, setNotifCounts] = useState(null);

  // Ask notification permission once when admin lands on the page
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((perm) => {
        notifPermissionRef.current = perm;
      });
    } else if ("Notification" in window) {
      notifPermissionRef.current = Notification.permission;
    }
  }, []);

  // Poll /admin/disputes/counts every 45 seconds
  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await apiClient.get("/admin/disputes/counts");
        if (stopped) return;
        setNotifCounts(data);
        const latest = data.latest_created_at;
        if (lastDisputeSeenRef.current && latest && latest > lastDisputeSeenRef.current) {
          // New dispute since last poll
          const openCount = data.counts.open || 0;
          const message = openCount > 0
            ? `${openCount} litige${openCount > 1 ? "s" : ""} en attente d'action`
            : "Un nouveau litige a été ouvert";
          toast.warning(`⚖️ Nouveau litige · ${message}`, { duration: 8000 });
          if ("Notification" in window && Notification.permission === "granted") {
            try {
              const n = new Notification("⚖️ Batisur — Nouveau litige", {
                body: message + "\nCliquez pour ouvrir l'onglet Litiges.",
                icon: "/favicon.ico",
                tag: "batisur-dispute",
                requireInteraction: true,
              });
              n.onclick = () => { window.focus(); setTab("disputes"); n.close(); };
            } catch { /* silent */ }
          }
        }
        lastDisputeSeenRef.current = latest;
      } catch {
        // silent — network hiccups shouldn't spam admin
      }
    };
    poll(); // initial
    const timer = setInterval(poll, 45000);
    return () => { stopped = true; clearInterval(timer); };
  }, []);

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async (e) => {
    e?.preventDefault();
    setSavingSettings(true);
    try {
      const payload = { ...settings };
      delete payload._key;
      delete payload.updated_at;
      delete payload.updated_by;
      // Coerce numeric fields
      if (payload.commission_rate !== "" && payload.commission_rate != null)
        payload.commission_rate = parseFloat(payload.commission_rate);
      if (payload.vat_rate !== "" && payload.vat_rate != null)
        payload.vat_rate = parseFloat(payload.vat_rate);
      if (payload.siret_required_min_amount !== "" && payload.siret_required_min_amount != null)
        payload.siret_required_min_amount = parseFloat(payload.siret_required_min_amount);
      if (payload.premium_price_eur !== "" && payload.premium_price_eur != null)
        payload.premium_price_eur = parseFloat(payload.premium_price_eur);
      if (payload.premium_days !== "" && payload.premium_days != null)
        payload.premium_days = parseInt(payload.premium_days, 10);
      if (payload.gold_price_eur !== "" && payload.gold_price_eur != null)
        payload.gold_price_eur = parseFloat(payload.gold_price_eur);
      if (payload.gold_min_completed_missions !== "" && payload.gold_min_completed_missions != null)
        payload.gold_min_completed_missions = parseInt(payload.gold_min_completed_missions, 10);
      const { data } = await apiClient.put("/admin/settings", payload);
      setSettings(data.settings || {});
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setSavingSettings(false);
    }
  };

  const updateUser = async (id, payload) => {
    try {
      await apiClient.patch(`/admin/users/${id}`, payload);
      toast.success("Utilisateur mis à jour");
      reload();
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} ? Cette action est définitive.`)) return;
    try {
      await apiClient.delete(`/admin/users/${id}`);
      toast.success("Utilisateur supprimé");
      reload();
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const markPaidOut = async (id) => {
    try {
      await apiClient.post(`/admin/missions/${id}/mark-paid-out`);
      toast.success("Marqué comme viré");
      const { data } = await apiClient.get("/admin/pending-payouts");
      setPendingPayouts(data);
      reload();
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const markArtisanPaidOut = async (group) => {
    if (!window.confirm(`Marquer ${group.missions_count} mission(s) de ${group.artisan_name} comme virées (${fmtEUR(group.total_due)}) ?`)) return;
    try {
      await Promise.all(group.missions.map((m) => apiClient.post(`/admin/missions/${m.id}/mark-paid-out`)));
      toast.success(`${group.missions_count} paiement(s) marqué(s) comme viré(s)`);
      const { data } = await apiClient.get("/admin/pending-payouts");
      setPendingPayouts(data);
      reload();
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const exportPayoutsCSV = () => {
    if (!pendingPayouts?.payouts?.length) {
      toast.error("Aucun versement en attente");
      return;
    }
    const rows = [
      ["Nom", "Entreprise", "SIRET", "Email", "Telephone", "IBAN", "BIC", "Missions", "Montant total EUR"],
    ];
    for (const g of pendingPayouts.payouts) {
      rows.push([
        g.artisan_name || "",
        g.artisan_company || "",
        g.artisan_siret || "",
        g.artisan_email || "",
        g.artisan_phone || "",
        g.iban || "",
        g.bic || "",
        g.missions_count,
        g.total_due.toFixed(2).replace(".", ","),
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `batisur-versements-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDemandeur = async (d) => {
    try {
      const { data } = await apiClient.get(`/admin/demandeurs/${d.id}`);
      setOpenedDemandeur(data);
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const openContact = (d) => {
    setContactTarget({ id: d.id, name: d.name || d.profile?.name, email: d.email || d.profile?.email });
    setContactForm({ subject: "", message: "" });
  };

  const sendContact = async () => {
    if (!contactTarget) return;
    if (contactForm.subject.trim().length < 2 || contactForm.message.trim().length < 5) {
      toast.error("Sujet (2+ car.) et message (5+ car.) requis");
      return;
    }
    setContactSending(true);
    try {
      const { data } = await apiClient.post(`/admin/demandeurs/${contactTarget.id}/contact`, contactForm);
      if (data.sent) toast.success(`Email envoyé à ${contactTarget.email}`);
      else toast.warning("Email loggé mais non envoyé (Resend inactif)");
      setContactTarget(null);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setContactSending(false);
    }
  };

  const runTrialSweep = async () => {
    setSweepingTrials(true);
    try {
      const { data } = await apiClient.post("/admin/nurture-trials-sweep");
      toast.success(`Balayage : ${data.sent} email(s) envoyé(s) / ${data.scanned} artisan(s) scannés`);
      const { data: stats } = await apiClient.get("/admin/trial-nurture-stats");
      setTrialStats(stats);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setSweepingTrials(false);
    }
  };

  const openProposalModal = (d) => {
    setProposalDispute(d);
    setProposalForm({
      proposal: d.admin_proposal?.text || "",
      demandeur_refund_pct: d.admin_proposal?.demandeur_refund_pct ?? 50,
    });
  };

  const applyProposalTemplate = (tpl) => {
    const templates = {
      partial: {
        demandeur_refund_pct: 30,
        proposal: "Après analyse du journal de chantier et des échanges, nous constatons que la majeure partie du travail a été effectuée mais qu'une reprise partielle est nécessaire. Nous proposons donc que l'artisan conserve 70% du montant séquestré en contrepartie du travail exécuté, et que 30% soient remboursés au demandeur pour couvrir la reprise ou le désagrément.",
      },
      full_refund: {
        demandeur_refund_pct: 100,
        proposal: "Après examen approfondi du dossier (journal photo, échanges messagerie, devis), nous constatons que la prestation n'a pas été délivrée conformément aux engagements du devis. Nous proposons un remboursement intégral au demandeur.",
      },
      compromise: {
        demandeur_refund_pct: 50,
        proposal: "Après analyse contradictoire du dossier, les torts sont partagés entre les deux parties. Dans un esprit de médiation amiable, nous proposons une répartition équitable 50/50 du montant séquestré.",
      },
    };
    const t = templates[tpl];
    if (t) setProposalForm(t);
  };

  const sendProposal = async () => {
    if (!proposalDispute) return;
    if (proposalForm.proposal.trim().length < 10) {
      toast.error("Justification obligatoire (10+ car.)");
      return;
    }
    setProposalSending(true);
    try {
      await apiClient.post(`/admin/disputes/${proposalDispute.id}/propose`, {
        proposal: proposalForm.proposal.trim(),
        demandeur_refund_pct: Number(proposalForm.demandeur_refund_pct),
      });
      toast.success("Proposition envoyée aux deux parties par email");
      const { data } = await apiClient.get("/admin/disputes");
      setDisputes(data);
      setProposalDispute(null);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setProposalSending(false);
    }
  };

  const closeDispute = async (id) => {
    if (!window.confirm("Clôturer ce litige ? Les fonds resteront bloqués jusqu'à décision de justice.")) return;
    try {
      const { data } = await apiClient.post(`/admin/disputes/${id}/close`);
      toast.success(`Litige clos (${data.outcome})`);
      const { data: fresh } = await apiClient.get("/admin/disputes");
      setDisputes(fresh);
    } catch (e) {
      toast.error(readError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Administration</div>
        <h1 className="display text-5xl mt-2">Console Batisur</h1>

        {/* STATS */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[#0A0A0B] bg-white">
            <Stat icon={Users} label="Utilisateurs" value={stats.users_count} sub={`${stats.demandeurs}D · ${stats.artisans}A`} />
            <Stat icon={Crown} label="Premium actifs" value={stats.premium_active} sub={`${fmtEUR(stats.premium_revenue)} générés`} />
            <Stat icon={ShieldCheck} label="Sous séquestre" value={fmtEUR(stats.escrow_total)} sub={`${stats.in_escrow} mission${stats.in_escrow>1?"s":""}`} accent />
            <Stat icon={Banknote} label="Commission totale" value={fmtEUR(stats.total_commission)} sub={`Volume ${fmtEUR(stats.volume)}`} />
          </div>
        )}
        {stats && (
          <div className="mt-0 grid grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-t-0 border-[#0A0A0B] bg-white">
            <Stat icon={Briefcase} label="Missions" value={stats.missions_count} sub={`${stats.completed} terminées`} small />
            <Stat icon={Layers} label="Chantiers" value={stats.chantiers_count} small />
            <Stat icon={Wallet} label="Payouts à virer" value={fmtEUR(stats.pending_payouts_total)} sub={`${stats.pending_payouts_count} artisan(s)`} small />
            <Stat icon={Banknote} label="Payouts cumulés" value={fmtEUR(stats.total_payouts)} small />
          </div>
        )}

        {/* Demandeurs by type breakdown */}
        {stats?.demandeurs_by_type && (
          <div className="mt-6 border-2 border-[#0A0A0B] bg-white p-5" data-testid="demandeurs-by-type">
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 mb-3">
              // Répartition des demandeurs · {stats.certificates_count} certificat{stats.certificates_count > 1 ? "s" : ""} Batisur émis
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {stats.demandeurs_by_type.map((row) => (
                <div
                  key={row.type}
                  className={`border-2 p-3 ${row.count > 0 ? "border-[#0A0A0B] bg-white" : "border-zinc-200 bg-zinc-50"}`}
                  data-testid={`stat-type-${row.type}`}
                >
                  <div className="display text-3xl">{row.count}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 mt-1 leading-tight">{row.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TABS */}
        <div className="mt-10 flex overflow-x-auto gap-0 border-2 border-[#0A0A0B] bg-white max-w-full">
          {[
            ["overview", "Vue d'ensemble"],
            ["users", "Utilisateurs"],
            ["demandeurs", "Demandeurs"],
            ["missions", "Missions"],
            ["payouts", "Payouts"],
            ["invoices", "Factures"],
            ["vat", "TVA"],
            ["trials", "Essais artisans"],
            ["disputes", "Litiges"],
            ["prospects", "Prospects villes"],
            ["txs", "Transactions"],
            ["rgpd", "Audit RGPD"],
            ["settings", "Paramètres"],
          ].map(([k, l]) => {
            const badge = k === "disputes" && notifCounts?.total_active > 0 ? notifCounts.total_active : null;
            return (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`px-4 sm:px-5 py-3 mono text-[10px] sm:text-[11px] uppercase tracking-widest border-r-2 border-[#0A0A0B] last:border-r-0 relative shrink-0 ${
                  tab === k ? "bg-[#0A0A0B] text-white" : "bg-white text-[#0A0A0B] hover:bg-zinc-100"
                }`}
                data-testid={`admin-tab-${k}`}
              >
                {l}
                {badge && (
                  <span className="ml-2 inline-flex items-center justify-center bg-[#FF4D00] text-white rounded-full min-w-[18px] h-[18px] px-1 text-[10px] font-bold animate-pulse" data-testid="disputes-badge-live">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4 border-2 border-[#0A0A0B] bg-white">
          {tab === "overview" && (
            <div className="p-6 grid lg:grid-cols-2 gap-6">
              <div className="border-2 border-[#0A0A0B] p-5">
                <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Identité</div>
                <div className="display text-2xl mt-2">{settings.company_name || "Société non renseignée"}</div>
                <div className="text-sm text-zinc-600 mt-1">
                  {settings.legal_form && `${settings.legal_form} · `}
                  {settings.siret ? `SIRET ${settings.siret}` : "SIRET manquant"}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <Mini label="IBAN" value={settings.iban || "—"} mono />
                  <Mini label="BIC" value={settings.bic || "—"} mono />
                  <Mini label="Banque" value={settings.bank_name || "—"} />
                  <Mini label="TVA" value={settings.vat_number || "—"} mono />
                </div>
                <button onClick={() => setTab("settings")} className="btn-anthracite mt-5 text-xs" data-testid="overview-edit-settings">
                  Modifier les paramètres →
                </button>
              </div>

              <div className="border-2 border-[#FF4D00] p-5">
                <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// TVA à reverser ce trimestre</div>
                <div className="display text-5xl text-[#FF4D00] mt-3">
                  {fmtEUR(vat?.current_quarter?.total_vat || 0)}
                </div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  {vat?.current_quarter?.label} · {vat?.current_quarter?.invoice_count || 0} facture(s)
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <Mini label="CA HT" value={fmtEUR(vat?.current_quarter?.total_ht || 0)} />
                  <Mini label="Trésorerie à virer" value={fmtEUR(stats?.pending_payouts_total || 0)} />
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={() => setTab("vat")} className="btn-batisur text-xs flex items-center gap-1" data-testid="overview-go-vat">
                    <Receipt className="w-3.5 h-3.5" /> Détail TVA
                  </button>
                  <button onClick={() => setTab("payouts")} className="btn-anthracite text-xs" data-testid="overview-go-payouts">
                    Payouts →
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === "overview" && (
            <div className="px-6 pb-6">
              <MissionInvitationsBackfillTool />
              <ProfileNudgeTool />
            </div>
          )}

          {tab === "users" && (
            <Table
              cols={["Nom", "Email", "Rôle", "Type / Pack", "Note", "Actions"]}
              rows={users.map((u) => [
                u.name,
                u.email,
                <span key="r" className="mono text-[11px] uppercase">{u.role}</span>,
                u.role === "artisan" ? (
                  <div key="p" className="flex flex-wrap gap-1">
                    {u.is_gold && (
                      <span className="badge-pill bg-amber-50 text-amber-700 border-amber-400">
                        Or · {u.gold_until?.slice(0, 10)}
                      </span>
                    )}
                    {u.is_premium && !u.is_gold && (
                      <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-300">
                        Premium · {u.premium_until?.slice(0, 10)}
                      </span>
                    )}
                    {!u.is_premium && !u.is_gold && (
                      <span className="badge-pill bg-white text-zinc-500 border-zinc-300">Standard</span>
                    )}
                  </div>
                ) : u.role === "demandeur" ? (
                  <span key="p" className="badge-pill bg-[#0A0A0B] text-white border-[#0A0A0B] mono text-[10px] uppercase tracking-widest" data-testid={`user-demandeur-type-${u.id}`}>
                    {DEMANDEUR_TYPE_LABEL[u.demandeur_type || "particulier"] || "Particulier"}
                  </span>
                ) : (
                  "—"
                ),
                u.rating_avg ? `${u.rating_avg.toFixed(1)} (${u.rating_count})` : "—",
                u.role !== "admin" && (
                  <div key="a" className="flex gap-1 flex-wrap">
                    {u.role === "artisan" && !u.is_premium && (
                      <button
                        onClick={() => updateUser(u.id, { premium_days: 30 })}
                        className="text-[10px] mono uppercase tracking-widest border border-[#FF4D00] text-[#FF4D00] hover:bg-[#FF4D00] hover:text-white px-2 py-1"
                        data-testid={`grant-premium-${u.id}`}
                      >
                        + 30j Premium
                      </button>
                    )}
                    {u.role === "artisan" && u.is_premium && !u.is_gold && (
                      <button
                        onClick={() => updateUser(u.id, { revoke_premium: true })}
                        className="text-[10px] mono uppercase tracking-widest border border-zinc-400 text-zinc-700 hover:bg-zinc-100 px-2 py-1"
                        data-testid={`revoke-premium-${u.id}`}
                      >
                        Révoquer Prem.
                      </button>
                    )}
                    {u.role === "artisan" && !u.is_gold && (
                      <button
                        onClick={() => updateUser(u.id, { gold_days: 30 })}
                        className="text-[10px] mono uppercase tracking-widest border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white px-2 py-1"
                        data-testid={`grant-gold-${u.id}`}
                      >
                        + 30j Or
                      </button>
                    )}
                    {u.role === "artisan" && u.is_gold && (
                      <button
                        onClick={() => updateUser(u.id, { revoke_gold: true })}
                        className="text-[10px] mono uppercase tracking-widest border border-amber-400 text-amber-800 hover:bg-amber-100 px-2 py-1"
                        data-testid={`revoke-gold-${u.id}`}
                      >
                        Révoquer Or
                      </button>
                    )}
                    <button
                      onClick={() => deleteUser(u.id, u.name)}
                      className="text-[10px] mono uppercase tracking-widest border border-red-300 text-red-700 hover:bg-red-600 hover:text-white px-2 py-1"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-3 h-3 inline" />
                    </button>
                  </div>
                ),
              ])}
            />
          )}

          {tab === "missions" && (
            <Table
              cols={["Titre", "Demandeur", "Catégorie", "Ville", "Montant", "Statut", ""]}
              rows={missions.map((m) => [
                m.title,
                m.demandeur_name,
                m.category,
                m.city,
                m.accepted_amount ? fmtEUR(m.accepted_amount) : `${fmtEUR(m.budget_min)}—${fmtEUR(m.budget_max)}`,
                <StatusBadge key="s" status={m.status} />,
                <button
                  key="del"
                  onClick={async () => {
                    if (!window.confirm(`Supprimer définitivement « ${m.title} » et toutes ses données (devis, messages, photos, factures) ?`)) return;
                    try {
                      await apiClient.delete(`/admin/missions/${m.id}`);
                      toast.success(`Mission « ${m.title} » supprimée`);
                      setMissions((prev) => prev.filter((x) => x.id !== m.id));
                    } catch (e) {
                      toast.error(readError(e));
                    }
                  }}
                  className="mono text-[10px] uppercase tracking-widest text-red-600 border border-red-300 px-2 py-1 hover:bg-red-600 hover:text-white transition-colors"
                  data-testid={`admin-delete-mission-${m.id}`}
                >
                  Supprimer
                </button>,
              ])}
            />
          )}

          {tab === "demandeurs" && (
            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className="grid lg:grid-cols-3 gap-0 border-2 border-[#0A0A0B]">
                <div className="p-6 border-r-2 border-[#0A0A0B] bg-[#0A0A0B] text-white">
                  <Users className="w-6 h-6 text-[#FF4D00]" />
                  <div className="mono text-[10px] uppercase tracking-widest mt-3 text-zinc-400">Demandeurs inscrits</div>
                  <div className="display text-4xl mt-1" data-testid="demandeurs-total-count">{demandeursData?.total_count || 0}</div>
                </div>
                <div className="p-6 border-r-2 border-[#0A0A0B] bg-[#FF4D00] text-white">
                  <Wallet className="w-6 h-6" />
                  <div className="mono text-[10px] uppercase tracking-widest mt-3 text-white/80">Volume total séquestré / dépensé</div>
                  <div className="display text-4xl mt-1" data-testid="demandeurs-total-volume">{fmtEUR(demandeursData?.total_volume || 0)}</div>
                </div>
                <div className="p-6 bg-white">
                  <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">Filtrer</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <button
                      onClick={() => setDemandeurFilter("all")}
                      className={`text-[10px] mono uppercase tracking-widest px-2 py-1 border ${demandeurFilter === "all" ? "bg-[#0A0A0B] text-white border-[#0A0A0B]" : "bg-white text-[#0A0A0B] border-zinc-300 hover:border-[#0A0A0B]"}`}
                      data-testid="demandeur-filter-all"
                    >Tous</button>
                    {Object.entries(DEMANDEUR_TYPE_LABEL).map(([k, l]) => (
                      <button
                        key={k}
                        onClick={() => setDemandeurFilter(k)}
                        className={`text-[10px] mono uppercase tracking-widest px-2 py-1 border ${demandeurFilter === k ? "bg-[#FF4D00] text-white border-[#FF4D00]" : "bg-white text-[#0A0A0B] border-zinc-300 hover:border-[#FF4D00]"}`}
                        data-testid={`demandeur-filter-${k}`}
                      >{l}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="border-2 border-[#0A0A0B] bg-white p-3 flex items-center gap-3">
                <Search className="w-4 h-4 text-zinc-500 ml-1" />
                <input
                  type="text"
                  value={demandeurSearch}
                  onChange={(e) => setDemandeurSearch(e.target.value)}
                  placeholder="Rechercher par nom, email ou ville…"
                  className="flex-1 outline-none text-sm bg-transparent"
                  data-testid="demandeur-search-input"
                />
                {demandeurSearch && (
                  <button onClick={() => setDemandeurSearch("")} className="text-zinc-400 hover:text-[#FF4D00]"><XIcon className="w-4 h-4" /></button>
                )}
              </div>

              {/* List */}
              {!demandeursData && <div className="p-8 text-center mono text-xs text-zinc-500">Chargement…</div>}
              {demandeursData && (() => {
                const filtered = demandeursData.demandeurs.filter((d) => {
                  if (demandeurFilter !== "all" && (d.demandeur_type || "particulier") !== demandeurFilter) return false;
                  if (demandeurSearch.trim()) {
                    const s = demandeurSearch.toLowerCase();
                    if (!(d.name || "").toLowerCase().includes(s)
                      && !(d.email || "").toLowerCase().includes(s)
                      && !(d.city || "").toLowerCase().includes(s)) return false;
                  }
                  return true;
                });
                if (filtered.length === 0) {
                  return <div className="border-2 border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500" data-testid="demandeur-empty">Aucun demandeur ne correspond aux critères.</div>;
                }
                return (
                  <div className="border-2 border-[#0A0A0B] bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#0A0A0B] text-white">
                        <tr>
                          <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Nom / Contact</th>
                          <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Type</th>
                          <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Ville</th>
                          <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Missions</th>
                          <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Chantiers</th>
                          <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Volume</th>
                          <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((d) => (
                          <tr key={d.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`demandeur-row-${d.id}`}>
                            <td className="px-4 py-3">
                              <div className="font-semibold">{d.name || "—"}</div>
                              <div className="text-xs text-zinc-500">{d.email}</div>
                              {d.company && <div className="text-xs text-zinc-500">{d.company}{d.siret ? ` · ${d.siret}` : ""}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <span className="badge-pill bg-[#0A0A0B] text-white border-[#0A0A0B] mono text-[10px] uppercase tracking-widest">{DEMANDEUR_TYPE_LABEL[d.demandeur_type] || "Particulier"}</span>
                            </td>
                            <td className="px-4 py-3 text-xs">{d.city || "—"}</td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold">{d.missions_count}</span>
                              {d.missions_in_progress > 0 && <span className="ml-2 badge-pill bg-amber-50 text-amber-700 border-amber-300">{d.missions_in_progress} en cours</span>}
                              {d.missions_completed > 0 && <span className="ml-1 badge-pill bg-emerald-50 text-emerald-700 border-emerald-300">{d.missions_completed} ✓</span>}
                            </td>
                            <td className="px-4 py-3 text-right">{d.chantiers_count}</td>
                            <td className="px-4 py-3 text-right font-semibold text-[#FF4D00]">{fmtEUR(d.total_spent)}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-1">
                                <Link
                                  to={`/admin/demandeurs/${d.id}`}
                                  className="text-[10px] mono uppercase tracking-widest border-2 border-[#0A0A0B] px-2 py-1 hover:bg-[#0A0A0B] hover:text-white flex items-center gap-1"
                                  data-testid={`demandeur-view-${d.id}`}
                                  title="Fiche complète"
                                >
                                  <Eye className="w-3 h-3" /> Fiche
                                </Link>
                                <button
                                  onClick={() => openContact(d)}
                                  className="text-[10px] mono uppercase tracking-widest border-2 border-[#FF4D00] text-[#FF4D00] px-2 py-1 hover:bg-[#FF4D00] hover:text-white flex items-center gap-1"
                                  data-testid={`demandeur-contact-${d.id}`}
                                  title="Contacter"
                                >
                                  <Mail className="w-3 h-3" /> Contacter
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}

          {tab === "payouts" && (
            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className="grid lg:grid-cols-3 gap-0 border-2 border-[#0A0A0B]">
                <div className="p-6 border-r-2 border-[#0A0A0B] bg-[#FF4D00] text-white">
                  <Banknote className="w-6 h-6" />
                  <div className="mono text-[10px] uppercase tracking-widest mt-3 text-white/80">Total à virer</div>
                  <div className="display text-4xl mt-1" data-testid="payouts-total-due">{fmtEUR(pendingPayouts?.total_owed || 0)}</div>
                </div>
                <div className="p-6 border-r-2 border-[#0A0A0B] bg-white">
                  <Users className="w-6 h-6 text-[#0A0A0B]" />
                  <div className="mono text-[10px] uppercase tracking-widest mt-3 text-zinc-500">Artisans concernés</div>
                  <div className="display text-4xl mt-1" data-testid="payouts-artisans-count">{pendingPayouts?.artisans_count || 0}</div>
                </div>
                <div className="p-6 bg-white flex flex-col justify-between">
                  <div>
                    <Briefcase className="w-6 h-6 text-[#0A0A0B]" />
                    <div className="mono text-[10px] uppercase tracking-widest mt-3 text-zinc-500">Missions terminées</div>
                    <div className="display text-4xl mt-1" data-testid="payouts-missions-count">{pendingPayouts?.missions_count || 0}</div>
                  </div>
                  <button
                    onClick={exportPayoutsCSV}
                    disabled={!pendingPayouts?.payouts?.length}
                    className="btn-batisur text-xs flex items-center gap-1.5 self-start mt-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="payouts-export-csv"
                  >
                    <Download className="w-3.5 h-3.5" /> Export CSV (SEPA)
                  </button>
                </div>
              </div>

              {/* Grouped list */}
              {!pendingPayouts && (
                <div className="p-8 text-center mono text-xs text-zinc-500">Chargement…</div>
              )}
              {pendingPayouts && pendingPayouts.payouts.length === 0 && (
                <div className="p-8 text-center border-2 border-dashed border-zinc-300" data-testid="payouts-empty">
                  <div className="mono text-[11px] uppercase tracking-widest text-emerald-700">// À jour</div>
                  <div className="mt-2 text-sm text-zinc-600">Aucun versement en attente. Tous les paiements sont à jour.</div>
                </div>
              )}
              {pendingPayouts && pendingPayouts.payouts.map((g) => (
                <div key={g.artisan_id} className="border-2 border-[#0A0A0B] bg-white" data-testid={`payout-group-${g.artisan_id}`}>
                  <div className="p-5 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="display text-xl">{g.artisan_name}</div>
                        {g.artisan_company && <span className="text-xs text-zinc-500">· {g.artisan_company}</span>}
                        <span className="badge-pill bg-amber-50 text-amber-700 border-amber-300">{g.missions_count} mission{g.missions_count > 1 ? "s" : ""}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs text-zinc-600">
                        <div><span className="mono uppercase text-[9px] text-zinc-400">SIRET</span><br/>{g.artisan_siret || "—"}</div>
                        <div><span className="mono uppercase text-[9px] text-zinc-400">Email</span><br/>{g.artisan_email || "—"}</div>
                        <div><span className="mono uppercase text-[9px] text-zinc-400">Tél.</span><br/>{g.artisan_phone || "—"}</div>
                        <div><span className="mono uppercase text-[9px] text-zinc-400">IBAN</span><br/><span className="mono">{g.iban || "— (non renseigné)"}</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Total dû</div>
                        <div className="display text-3xl text-[#FF4D00]">{fmtEUR(g.total_due)}</div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => setExpandedArtisan(expandedArtisan === g.artisan_id ? null : g.artisan_id)}
                          className="text-[10px] mono uppercase tracking-widest border-2 border-[#0A0A0B] px-3 py-1.5 hover:bg-zinc-100"
                          data-testid={`payout-toggle-${g.artisan_id}`}
                        >
                          {expandedArtisan === g.artisan_id ? "Masquer" : "Détail"}
                        </button>
                        <button
                          onClick={() => markArtisanPaidOut(g)}
                          className="btn-anthracite text-[10px]"
                          data-testid={`payout-mark-all-${g.artisan_id}`}
                        >
                          Tout marquer viré
                        </button>
                      </div>
                    </div>
                  </div>
                  {expandedArtisan === g.artisan_id && (
                    <div className="border-t-2 border-[#0A0A0B] bg-zinc-50">
                      <Table
                        cols={["Mission", "Montant accepté", "Commission", "Payout net", "Terminée le", "Action"]}
                        rows={g.missions.map((m) => [
                          m.title || "—",
                          fmtEUR(m.accepted_amount),
                          fmtEUR(m.total_commission),
                          <strong key="p">{fmtEUR(m.artisan_payout)}</strong>,
                          (m.completed_at || "").slice(0, 10),
                          <button
                            key="a"
                            onClick={() => markPaidOut(m.id)}
                            className="text-[10px] mono uppercase tracking-widest border border-[#0A0A0B] px-2 py-1 hover:bg-[#0A0A0B] hover:text-white"
                            data-testid={`mark-paid-${m.id}`}
                          >
                            Marquer viré
                          </button>,
                        ])}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "invoices" && (
            <Table
              cols={["N°", "Date", "Type", "Destinataire", "Montant TTC", "Statut", "PDF"]}
              rows={invoices.map((inv) => [
                <span key="n" className="mono font-semibold">{inv.number}</span>,
                (inv.issued_at || "").slice(0, 10),
                inv.type === "premium" ? (
                  <span className="badge-pill bg-[#FF4D00] text-white border-[#FF4D00]">Premium</span>
                ) : inv.type === "commission_demandeur" ? (
                  <span className="badge-pill bg-white text-zinc-700 border-zinc-300">Commission D.</span>
                ) : (
                  <span className="badge-pill bg-zinc-100 text-zinc-700 border-zinc-300">Commission A.</span>
                ),
                inv.recipient?.name || "—",
                <strong key="t">{fmtEUR(inv.total_ttc)}</strong>,
                <span key="s" className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-300">{inv.status}</span>,
                <a
                  key="d"
                  href={`${BACKEND_URL}/api/invoices/${inv.id}/pdf`}
                  download={`${inv.number}.pdf`}
                  className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest border-2 border-[#0A0A0B] px-2 py-1 hover:bg-[#0A0A0B] hover:text-white"
                  data-testid={`invoice-pdf-${inv.id}`}
                >
                  <Download className="w-3 h-3" /> Télécharger
                </a>,
              ])}
            />
          )}

          {tab === "vat" && vat && (
            <div className="p-6 space-y-6">
              <div className="grid lg:grid-cols-3 gap-0 border-2 border-[#0A0A0B]">
                <div className="p-6 border-r-2 border-[#0A0A0B] bg-[#FF4D00] text-white">
                  <Receipt className="w-6 h-6" />
                  <div className="mono text-[10px] uppercase tracking-widest mt-3 text-white/80">TVA à reverser · {vat.current_quarter.label}</div>
                  <div className="display text-5xl mt-1">{fmtEUR(vat.current_quarter.total_vat)}</div>
                  <div className="mono text-[10px] uppercase tracking-widest mt-1 text-white/70">
                    Sur {vat.current_quarter.invoice_count} facture(s)
                  </div>
                </div>
                <div className="p-6 border-r-2 border-[#0A0A0B]">
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">CA HT · {vat.current_quarter.label}</div>
                  <div className="display text-3xl mt-2">{fmtEUR(vat.current_quarter.total_ht)}</div>
                </div>
                <div className="p-6">
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">TTC encaissé · {vat.current_quarter.label}</div>
                  <div className="display text-3xl mt-2">{fmtEUR(vat.current_quarter.total_ttc)}</div>
                </div>
              </div>

              <div className="border-2 border-[#0A0A0B]">
                <div className="px-5 py-3 bg-zinc-100 border-b-2 border-[#0A0A0B] flex items-center justify-between">
                  <div className="mono text-[11px] uppercase tracking-widest">// Historique trimestriel</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Cumul depuis lancement · TVA collectée {fmtEUR(vat.cumulative.total_vat)}
                  </div>
                </div>
                <Table
                  cols={["Trimestre", "Factures", "CA HT", "TVA collectée", "TTC encaissé"]}
                  rows={vat.quarters.map((q) => [
                    <strong key="l">{q.label}</strong>,
                    q.invoice_count,
                    fmtEUR(q.total_ht),
                    <span key="v" className="text-[#FF4D00] font-semibold">{fmtEUR(q.total_vat)}</span>,
                    fmtEUR(q.total_ttc),
                  ])}
                />
              </div>

              <div className="border-2 border-amber-300 bg-amber-50 p-5">
                <div className="mono text-[11px] uppercase tracking-widest text-amber-700">// Rappel CA3</div>
                <div className="text-sm text-amber-900 mt-2">
                  Les redevables à la TVA doivent déposer leur <strong>CA3</strong> (TVA collectée — TVA déductible)
                  à l'échéance mensuelle ou trimestrielle selon votre régime. Le montant ci-dessus correspond à la
                  <strong> TVA collectée</strong> par Batisur ; pensez à déduire votre TVA sur achats avant déclaration.
                </div>
              </div>
            </div>
          )}

          {tab === "trials" && (
            <div className="p-6 space-y-6" data-testid="trials-tab-content">
              <div className="border-2 border-[#0A0A0B] p-5 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Nurturing essais artisans</div>
                  <div className="display text-2xl mt-1">Funnel du 1er mois offert</div>
                  <div className="text-xs text-zinc-600 mt-2 max-w-2xl">
                    Chaque nouvel artisan reçoit 30 jours de Pack Pro offerts. Le système envoie automatiquement
                    6 emails de relance (J+3, J+7, J+14, J+21, J+27, J+31) à chaque connexion. Utilisez
                    "Lancer le balayage" pour forcer un envoi immédiat sans attendre les connexions.
                  </div>
                </div>
                <button
                  onClick={runTrialSweep}
                  disabled={sweepingTrials}
                  className="btn-batisur flex items-center gap-2 disabled:opacity-40"
                  data-testid="trial-sweep-run"
                >
                  <Send className="w-4 h-4" />
                  {sweepingTrials ? "Envoi…" : "Lancer le balayage"}
                </button>
              </div>

              {!trialStats ? (
                <div className="p-8 text-center mono text-xs text-zinc-500">Chargement…</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-2 border-[#0A0A0B] bg-white">
                    <Stat icon={Users} label="Essais accordés" value={trialStats.total_trials_granted} sub="Total historique" />
                    <Stat icon={Sparkles} label="Essais en cours" value={trialStats.active_trials} sub="Pack Pro actif" accent />
                    <Stat icon={Crown} label="Essais expirés" value={trialStats.expired_trials} sub="À convertir" />
                  </div>

                  <div className="border-2 border-[#0A0A0B] bg-white overflow-hidden">
                    <div className="bg-[#0A0A0B] text-white px-5 py-3 mono text-[11px] uppercase tracking-widest">
                      // Emails de relance envoyés
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50">
                        <tr>
                          <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Événement</th>
                          <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Déclencheur</th>
                          <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Envoyés</th>
                          <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Conversion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["artisan.welcome_trial", "J+0 · Bienvenue", "Inscription"],
                          ["artisan.trial.day3", "J+3 · Complétez votre profil", "Automatique"],
                          ["artisan.trial.day7", "J+7 · Optimisez votre visibilité", "Automatique"],
                          ["artisan.trial.day14", "J+14 · Chiffres clés Batisur", "Automatique"],
                          ["artisan.trial.day21", "J+21 · 9 jours restants", "Automatique"],
                          ["artisan.trial.day27", "J+27 · 3 jours restants ⏳", "Automatique"],
                          ["artisan.trial.expired", "J+31 · Essai terminé", "Automatique"],
                        ].map(([ev, label, trig]) => {
                          const count = trialStats.emails_sent_by_event?.[ev] || 0;
                          const pct = trialStats.total_trials_granted > 0
                            ? Math.round((count / trialStats.total_trials_granted) * 100)
                            : 0;
                          return (
                            <tr key={ev} className="border-t border-zinc-200" data-testid={`trial-row-${ev}`}>
                              <td className="px-4 py-3 mono text-xs">{ev}</td>
                              <td className="px-4 py-3 font-medium">{label}</td>
                              <td className="px-4 py-3 text-right">
                                <span className="mono text-xs text-zinc-500">Auto ·</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <strong className="text-[#FF4D00]">{count}</strong>
                                <span className="mono text-[10px] text-zinc-500 ml-2">{pct}% du total</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "disputes" && (
            <div className="p-6 space-y-6" data-testid="disputes-tab-content">
              {/* Header + counts */}
              <div className="border-2 border-[#0A0A0B] p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Scale className="w-7 h-7 text-[#FF4D00] shrink-0 mt-1" />
                    <div>
                      <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Service de médiation</div>
                      <div className="display text-2xl mt-1">Litiges en cours</div>
                      <div className="text-xs text-zinc-600 mt-2 max-w-3xl">
                        Chaque litige a un délai légal de <strong>30 jours</strong> pour aboutir à un accord amiable (CGU 6.2). Au-delà, les fonds restent bloqués sur le compte Stripe Express de l'artisan jusqu'à décision de justice ou accord écrit signé.
                        Un balayage automatique s'exécute toutes les 6 heures pour marquer les litiges expirés.
                      </div>
                      <div className="mt-3 flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-emerald-700">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                        Notifications live actives · polling toutes les 45s
                        {notifPermissionRef.current === "granted" && <span className="ml-2 text-zinc-500">· push navigateur autorisé</span>}
                        {notifPermissionRef.current === "denied" && <span className="ml-2 text-red-600">· push navigateur bloqué</span>}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await apiClient.post("/admin/disputes/auto-close-expired");
                        toast.success(`${data.closed_count} litige(s) expiré(s) marqué(s)`);
                        const { data: fresh } = await apiClient.get("/admin/disputes");
                        setDisputes(fresh);
                      } catch (e) { toast.error(readError(e)); }
                    }}
                    className="btn-anthracite text-xs flex items-center gap-1.5 shrink-0"
                    data-testid="disputes-auto-close-run"
                  >
                    <AlertTriangle className="w-3.5 h-3.5" /> Forcer balayage 30j+
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-0 border-2 border-[#0A0A0B] mt-5">
                  <div className="p-4 border-r-2 border-[#0A0A0B] bg-[#FF4D00] text-white text-center">
                    <div className="display text-3xl" data-testid="disputes-count-open">{disputes?.counts?.open || 0}</div>
                    <div className="mono text-[10px] uppercase tracking-widest mt-1">Ouverts</div>
                  </div>
                  <div className="p-4 border-r-2 border-[#0A0A0B] bg-amber-50 text-center">
                    <div className="display text-3xl text-amber-800" data-testid="disputes-count-mediation">{disputes?.counts?.mediation || 0}</div>
                    <div className="mono text-[10px] uppercase tracking-widest mt-1 text-amber-700">En médiation</div>
                  </div>
                  <div className="p-4 border-r-2 border-[#0A0A0B] bg-emerald-50 text-center">
                    <div className="display text-3xl text-emerald-800" data-testid="disputes-count-resolved">{disputes?.counts?.resolved || 0}</div>
                    <div className="mono text-[10px] uppercase tracking-widest mt-1 text-emerald-700">Résolus</div>
                  </div>
                  <div className="p-4 bg-zinc-100 text-center">
                    <div className="display text-3xl text-zinc-700" data-testid="disputes-count-expired">{disputes?.counts?.expired || 0}</div>
                    <div className="mono text-[10px] uppercase tracking-widest mt-1 text-zinc-600">Expirés / clos</div>
                  </div>
                </div>
              </div>

              {!disputes ? (
                <div className="p-8 text-center mono text-xs text-zinc-500">Chargement…</div>
              ) : disputes.disputes.length === 0 ? (
                <div className="border-2 border-dashed border-zinc-300 p-8 text-center" data-testid="disputes-empty">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                  <div className="mono text-[11px] uppercase tracking-widest text-emerald-700 mt-3">// Aucun litige actif</div>
                  <div className="text-sm text-zinc-600 mt-1">Tous les chantiers en cours se déroulent sans conflit.</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {disputes.disputes.map((d) => {
                    const total = 30;
                    const elapsed = Math.min(d.elapsed_days, total);
                    const pct = (elapsed / total) * 100;
                    const barColor = d.days_remaining <= 3 ? "bg-red-600" : d.days_remaining <= 10 ? "bg-amber-500" : "bg-[#FF4D00]";
                    const statusPill = {
                      open: "bg-[#FF4D00] text-white border-[#FF4D00]",
                      mediation: "bg-amber-50 text-amber-800 border-amber-400",
                      resolved: "bg-emerald-50 text-emerald-800 border-emerald-400",
                      expired: "bg-zinc-100 text-zinc-700 border-zinc-400",
                      closed_admin: "bg-zinc-100 text-zinc-700 border-zinc-400",
                    }[d.status] || "bg-white text-zinc-700 border-zinc-300";
                    const statusLabel = {
                      open: "OUVERT",
                      mediation: "EN MÉDIATION",
                      resolved: "RÉSOLU",
                      expired: "EXPIRÉ",
                      closed_admin: "CLOS ADMIN",
                    }[d.status] || d.status.toUpperCase();

                    return (
                      <div key={d.id} className="border-2 border-[#0A0A0B] bg-white" data-testid={`dispute-card-${d.id}`}>
                        <div className="p-5 flex flex-col lg:flex-row gap-4 justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`badge-pill ${statusPill}`}>{statusLabel}</span>
                              <div className="display text-xl">{d.mission?.title || "—"}</div>
                              <span className="text-xs text-zinc-500">· {d.mission?.city || "—"} · {d.mission?.category || "—"}</span>
                            </div>
                            <div className="mt-2 text-sm text-zinc-700">
                              <strong>{fmtEUR(d.amount_escrow || 0)}</strong> sous séquestre · ouvert par <strong>{d.opened_by_name}</strong> ({d.opened_by_role})
                            </div>
                            <div className="mt-3 grid md:grid-cols-2 gap-2 text-xs">
                              <div className="border border-zinc-200 p-2">
                                <div className="flex items-center justify-between">
                                  <span className="mono uppercase text-[9px] text-zinc-400">Demandeur</span>
                                  {d.demandeur?.past_disputes_12m > 0 && (
                                    <span className="badge-pill bg-red-50 text-red-700 border-red-300 text-[9px]" data-testid={`demandeur-history-${d.id}`}>
                                      ⚠ {d.demandeur.past_disputes_12m} litige{d.demandeur.past_disputes_12m > 1 ? "s" : ""} 12m
                                    </span>
                                  )}
                                </div>
                                <strong>{d.demandeur?.name || "—"}</strong> · {d.demandeur?.email || "—"}
                              </div>
                              <div className="border border-zinc-200 p-2">
                                <div className="flex items-center justify-between">
                                  <span className="mono uppercase text-[9px] text-zinc-400">Artisan</span>
                                  {d.artisan?.past_disputes_12m > 0 && (
                                    <Link
                                      to={`/admin/artisans/${d.artisan.id}`}
                                      className="badge-pill bg-red-50 text-red-700 border-red-300 text-[9px] hover:bg-red-100 cursor-pointer"
                                      data-testid={`artisan-history-${d.id}`}
                                    >
                                      ⚠ {d.artisan.past_disputes_12m} litige{d.artisan.past_disputes_12m > 1 ? "s" : ""} 12m →
                                    </Link>
                                  )}
                                </div>
                                <strong>{d.artisan?.name || "—"}</strong>{d.artisan?.company ? ` · ${d.artisan.company}` : ""} · {d.artisan?.email || "—"}
                              </div>
                            </div>
                            <div className="mt-3 border-l-4 border-[#FF4D00] pl-3 py-1 bg-zinc-50 text-sm italic">
                              &quot;{d.reason}&quot;
                            </div>

                            {/* Timeline visuelle */}
                            {(() => {
                              const steps = [
                                { key: "opened", label: "Ouverture", date: d.created_at, done: true },
                                { key: "proposal", label: "Proposition médiation", date: d.admin_proposed_at, done: !!d.admin_proposal },
                                { key: "dem_response", label: "Réponse demandeur", date: d.demandeur_response?.responded_at, done: !!d.demandeur_response, accepted: d.demandeur_response?.accepted },
                                { key: "art_response", label: "Réponse artisan", date: d.artisan_response?.responded_at, done: !!d.artisan_response, accepted: d.artisan_response?.accepted },
                                { key: "resolved", label: {
                                  resolved: "Accord amiable ✓",
                                  expired: "Expiré (30j+)",
                                  closed_admin: "Clos par admin",
                                }[d.status] || "En attente clôture", date: d.resolved_at, done: !!d.resolved_at },
                              ];
                              return (
                                <div className="mt-4 border-2 border-zinc-200 bg-zinc-50 p-3" data-testid={`dispute-timeline-${d.id}`}>
                                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">// Timeline</div>
                                  <div className="flex items-center gap-1 overflow-x-auto">
                                    {steps.map((s, idx) => (
                                      <div key={s.key} className="flex items-center gap-1 flex-shrink-0">
                                        <div className="flex flex-col items-center min-w-[80px]">
                                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold ${
                                            s.done
                                              ? (s.accepted === false ? "bg-red-600 border-red-600 text-white" : "bg-[#FF4D00] border-[#FF4D00] text-white")
                                              : "bg-white border-zinc-300 text-zinc-400"
                                          }`}>
                                            {s.done ? (s.accepted === false ? "✗" : "✓") : idx + 1}
                                          </div>
                                          <div className={`mono text-[9px] uppercase tracking-widest mt-1 text-center leading-tight ${s.done ? "text-[#0A0A0B]" : "text-zinc-400"}`}>{s.label}</div>
                                          {s.date && <div className="mono text-[8px] text-zinc-400 mt-0.5">{s.date.slice(0, 10)}</div>}
                                        </div>
                                        {idx < steps.length - 1 && (
                                          <div className={`h-0.5 flex-1 min-w-[16px] ${steps[idx + 1].done ? "bg-[#FF4D00]" : "bg-zinc-300"}`} />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          <div className="w-full lg:w-72 shrink-0 space-y-3">
                            {/* Chrono */}
                            {d.status === "open" || d.status === "mediation" ? (
                              <div>
                                <div className="flex justify-between mono text-[10px] uppercase tracking-widest text-zinc-500">
                                  <span>J+{elapsed}</span>
                                  <span className={d.days_remaining <= 3 ? "text-red-600 font-bold" : "text-[#FF4D00]"}>{d.days_remaining}j restants</span>
                                </div>
                                <div className="mt-1 h-3 border-2 border-[#0A0A0B] bg-white relative overflow-hidden">
                                  <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                                </div>
                                <div className="mono text-[9px] uppercase tracking-widest text-zinc-400 mt-1">
                                  Deadline : {(d.deadline_at || "").slice(0, 10)}
                                </div>
                              </div>
                            ) : (
                              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                                Clos le {(d.resolved_at || "").slice(0, 10)}
                              </div>
                            )}

                            {/* Party responses */}
                            {d.status === "mediation" && d.admin_proposal && (
                              <div className="border-2 border-amber-400 bg-amber-50 p-3 text-xs">
                                <div className="mono uppercase text-[10px] tracking-widest text-amber-800">// Réponses parties</div>
                                <div className="mt-1">Demandeur : {d.demandeur_response ? (d.demandeur_response.accepted ? "✅ Accepté" : "❌ Refusé") : "⏳ En attente"}</div>
                                <div>Artisan : {d.artisan_response ? (d.artisan_response.accepted ? "✅ Accepté" : "❌ Refusé") : "⏳ En attente"}</div>
                                <div className="mt-2 border-t border-amber-300 pt-2">
                                  Proposition : <strong>{d.admin_proposal.demandeur_refund_pct}% ↩ D. / {d.admin_proposal.artisan_share_pct}% → A.</strong>
                                </div>
                              </div>
                            )}

                            {/* Actions */}
                            {(d.status === "open" || d.status === "mediation") && (
                              <div className="flex flex-col gap-2">
                                <button
                                  onClick={() => openProposalModal(d)}
                                  className="btn-batisur flex items-center justify-center gap-1.5 text-xs"
                                  data-testid={`dispute-propose-${d.id}`}
                                >
                                  <Send className="w-3.5 h-3.5" />
                                  {d.status === "mediation" ? "Modifier proposition" : "Proposer un accord"}
                                </button>
                                <button
                                  onClick={() => closeDispute(d.id)}
                                  className="btn-anthracite text-xs flex items-center justify-center gap-1.5"
                                  data-testid={`dispute-close-${d.id}`}
                                >
                                  <AlertTriangle className="w-3.5 h-3.5" />
                                  {d.auto_expired ? "Constater expiration" : "Clôturer"}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "prospects" && (
            <div className="p-6 space-y-6" data-testid="admin-prospects-panel">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Villes à conquérir</div>
                  <h2 className="display text-2xl mt-1">Prospects capturés sur les villes prospectées</h2>
                  <p className="text-sm text-zinc-600 mt-1">
                    Ces prospects se sont inscrits sur des pages villes sans artisan actif — priorisez votre acquisition d'artisans par volume.
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">// Total</div>
                  <div className="display text-3xl">{prospects?.total_prospects ?? "—"}</div>
                  <div className="text-xs text-zinc-500">{prospects?.total_cities ?? 0} villes</div>
                </div>
              </div>

              {!prospects && <div className="mono text-sm text-zinc-500">Chargement…</div>}

              {prospects && prospects.aggregated.length === 0 && (
                <div className="border-2 border-dashed border-zinc-300 p-10 text-center">
                  <div className="display text-2xl">Aucun prospect capturé pour le moment</div>
                  <p className="text-sm text-zinc-500 mt-2">Les formulaires apparaissent sur les pages villes prospectées (ex : `/artisan/plombier/marseille`).</p>
                </div>
              )}

              {prospects && prospects.aggregated.length > 0 && (
                <>
                  <div>
                    <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold mb-3">// Top villes prioritaires (demande locale)</div>
                    <Table
                      cols={["Ville", "Prospects uniques", "Demandeurs / Artisans", "Top métiers demandés", "Dernier"]}
                      rows={prospects.aggregated.map((a) => [
                        <div key="c" className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-[#FF4D00] rounded-full" />
                          <span className="font-semibold">{a.city_label}</span>
                          <span className="mono text-[10px] uppercase text-zinc-400">{a.city_slug}</span>
                        </div>,
                        <span key="u" className="display text-lg">{a.unique_emails}</span>,
                        <div key="r" className="flex gap-2 text-xs">
                          <span className="border border-[#0A0A0B] px-2 py-0.5">D {a.roles.demandeur}</span>
                          <span className="border border-[#FF4D00] text-[#FF4D00] px-2 py-0.5">A {a.roles.artisan}</span>
                        </div>,
                        <div key="m" className="flex flex-wrap gap-1">
                          {a.top_metiers.length === 0 ? (
                            <span className="text-xs text-zinc-400">—</span>
                          ) : a.top_metiers.map((m) => (
                            <span key={m.slug} className="mono text-[10px] uppercase tracking-widest border border-zinc-300 px-2 py-0.5">
                              {m.slug} · {m.count}
                            </span>
                          ))}
                        </div>,
                        <span key="l" className="mono text-[10px] text-zinc-500">{a.last_at ? new Date(a.last_at).toLocaleDateString("fr-FR") : "—"}</span>,
                      ])}
                    />
                  </div>

                  <div>
                    <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">// 200 derniers prospects (détail)</div>
                    <Table
                      cols={["Email", "Ville", "Contexte", "Métier / Travaux", "Rôle", "Date"]}
                      rows={prospects.prospects.map((p) => [
                        <span key="e" className="text-sm">{p.email}</span>,
                        <span key="c">{p.city_label}</span>,
                        <span key="ctx" className="mono text-[10px] uppercase text-zinc-500">{p.context}</span>,
                        <span key="m" className="mono text-[10px] uppercase text-zinc-500">
                          {p.metier_slug || p.travaux_slug || p.persona_slug || "—"}
                        </span>,
                        <span key="r" className={`badge-pill ${p.role === "artisan" ? "bg-[#FF4D00] text-white border-[#FF4D00]" : "bg-white text-[#0A0A0B] border-zinc-300"}`}>
                          {p.role === "artisan" ? "Artisan" : "Demandeur"}
                        </span>,
                        <span key="d" className="mono text-[10px] text-zinc-500">{new Date(p.created_at).toLocaleString("fr-FR")}</span>,
                      ])}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "txs" && (
            <Table
              cols={["Session", "Type", "Montant", "Paiement", "Escrow", "Date"]}
              rows={txs.map((t) => [
                <span key="s" className="mono text-xs">{t.session_id?.slice(0, 14)}…</span>,
                t.type === "premium" ? (
                  <span className="badge-pill bg-[#FF4D00] text-white border-[#FF4D00]">Premium</span>
                ) : (
                  <span className="badge-pill bg-white text-zinc-700 border-zinc-300">Escrow</span>
                ),
                fmtEUR(t.amount),
                t.payment_status,
                t.escrow_status,
                (t.created_at || "").slice(0, 10),
              ])}
            />
          )}

          {tab === "rgpd" && (
            <div className="p-6 space-y-6" data-testid="rgpd-tab">
              {!rgpd && <div className="mono text-xs text-zinc-500">Chargement…</div>}
              {rgpd && (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="border-2 border-[#0A0A0B] p-4">
                      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Comptes totaux</div>
                      <div className="display text-3xl mt-1">{rgpd.total_users}</div>
                    </div>
                    <div className="border-2 border-emerald-600 p-4 bg-emerald-50">
                      <div className="mono text-[10px] uppercase tracking-widest text-emerald-700">CGU acceptées</div>
                      <div className="display text-3xl mt-1 text-emerald-700">{rgpd.accepted_count}</div>
                    </div>
                    <div className="border-2 border-amber-500 p-4 bg-amber-50">
                      <div className="mono text-[10px] uppercase tracking-widest text-amber-700">Sans horodatage (pré-CGU)</div>
                      <div className="display text-3xl mt-1 text-amber-700">{rgpd.missing_count}</div>
                    </div>
                    <div className="border-2 border-[#FF4D00] p-4 bg-orange-50">
                      <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">Version en vigueur</div>
                      <div className="display text-3xl mt-1">{rgpd.current_version}</div>
                    </div>
                  </div>

                  <div className="border-l-4 border-[#FF4D00] bg-zinc-50 p-4 text-xs text-zinc-700 flex items-start gap-3">
                    <ScrollText className="w-4 h-4 text-[#FF4D00] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong>Registre opposable.</strong> Pour chaque utilisateur inscrit après le 2026-02-24, l'acceptation des CGU/CGV/Mentions légales
                      est horodatée (UTC) et accompagnée de l'adresse IP et du User-Agent au moment de la création du compte. Document opposable en cas
                      de litige ou de contrôle CNIL (art. 5.2 et 30 du RGPD).
                    </div>
                  </div>

                  <Table
                    cols={["Email", "Nom", "Rôle", "Accepté le", "Version", "IP", "User-Agent"]}
                    rows={rgpd.users.map((u) => [
                      <span key="e" className="mono text-xs">{u.email}</span>,
                      u.name || "—",
                      <span key="r" className="badge-pill bg-white text-zinc-700 border-zinc-300 capitalize">{u.role}</span>,
                      u.terms_accepted_at
                        ? <span key="d" className="mono text-xs text-emerald-700">{new Date(u.terms_accepted_at).toLocaleString("fr-FR")}</span>
                        : <span key="d" className="mono text-xs text-amber-600">— (pré-CGU)</span>,
                      u.terms_version || "—",
                      <span key="ip" className="mono text-[10px] text-zinc-500">{u.terms_accepted_ip || "—"}</span>,
                      <span key="ua" className="mono text-[10px] text-zinc-400 truncate block max-w-xs" title={u.terms_accepted_ua || ""}>
                        {(u.terms_accepted_ua || "—").slice(0, 40)}{u.terms_accepted_ua && u.terms_accepted_ua.length > 40 ? "…" : ""}
                      </span>,
                    ])}
                  />
                </>
              )}
            </div>
          )}

          {tab === "settings" && (
            <form onSubmit={saveSettings} className="p-6 grid lg:grid-cols-12 gap-6" data-testid="settings-form">
              {/* Société */}
              <Section icon={Building2} title="Identité société" colSpan={6}>
                <Field label="Raison sociale" value={settings.company_name} onChange={(v) => setField("company_name", v)} testid="set-company-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Forme juridique" value={settings.legal_form} onChange={(v) => setField("legal_form", v)} placeholder="SAS, SARL…" testid="set-legal-form" />
                  <Field label="Capital social" value={settings.capital} onChange={(v) => setField("capital", v)} placeholder="ex: 10 000 €" testid="set-capital" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SIRET" value={settings.siret} onChange={(v) => setField("siret", v)} mono testid="set-siret" />
                  <Field label="SIREN" value={settings.siren} onChange={(v) => setField("siren", v)} mono testid="set-siren" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="N° TVA intra." value={settings.vat_number} onChange={(v) => setField("vat_number", v)} mono testid="set-vat" />
                  <Field label="RCS" value={settings.rcs} onChange={(v) => setField("rcs", v)} mono testid="set-rcs" />
                </div>
              </Section>

              {/* Adresse */}
              <Section icon={Building2} title="Siège social" colSpan={6}>
                <Field label="Adresse" value={settings.address_line1} onChange={(v) => setField("address_line1", v)} testid="set-address1" />
                <Field label="Complément" value={settings.address_line2} onChange={(v) => setField("address_line2", v)} testid="set-address2" />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Code postal" value={settings.postal_code} onChange={(v) => setField("postal_code", v)} mono testid="set-postal" />
                  <Field label="Ville" value={settings.city} onChange={(v) => setField("city", v)} testid="set-city" />
                  <Field label="Pays" value={settings.country} onChange={(v) => setField("country", v)} testid="set-country" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Email contact" value={settings.contact_email} onChange={(v) => setField("contact_email", v)} testid="set-email" />
                  <Field label="Téléphone" value={settings.contact_phone} onChange={(v) => setField("contact_phone", v)} testid="set-phone" />
                </div>
              </Section>

              {/* RIB */}
              <Section icon={Banknote} title="Coordonnées bancaires (RIB)" colSpan={6} highlight>
                <Field label="IBAN" value={settings.iban} onChange={(v) => setField("iban", v)} mono placeholder="FR76 …" testid="set-iban" />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="BIC / SWIFT" value={settings.bic} onChange={(v) => setField("bic", v)} mono testid="set-bic" />
                  <Field label="Nom de la banque" value={settings.bank_name} onChange={(v) => setField("bank_name", v)} testid="set-bank" />
                </div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                  Stocké chiffré côté serveur. Visible uniquement par les admins.
                </div>
              </Section>

              {/* Tarification */}
              <Section icon={Star} title="Tarification plateforme" colSpan={6}>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Commission (décimal)"
                    value={settings.commission_rate ?? ""}
                    onChange={(v) => setField("commission_rate", v)}
                    placeholder="0.10 = 10%"
                    mono
                    testid="set-commission"
                  />
                  <Field
                    label="TVA (décimal)"
                    value={settings.vat_rate ?? ""}
                    onChange={(v) => setField("vat_rate", v)}
                    placeholder="0.20 = 20%"
                    mono
                    testid="set-vat-rate"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Prix Premium (€)"
                    value={settings.premium_price_eur ?? ""}
                    onChange={(v) => setField("premium_price_eur", v)}
                    placeholder="29"
                    mono
                    testid="set-premium-price"
                  />
                  <Field
                    label="Durée Premium (jours)"
                    value={settings.premium_days ?? ""}
                    onChange={(v) => setField("premium_days", v)}
                    placeholder="30"
                    mono
                    testid="set-premium-days"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Prix Or (€)"
                    value={settings.gold_price_eur ?? ""}
                    onChange={(v) => setField("gold_price_eur", v)}
                    placeholder="49"
                    mono
                    testid="set-gold-price"
                  />
                  <Field
                    label="Missions min. Or"
                    value={settings.gold_min_completed_missions ?? ""}
                    onChange={(v) => setField("gold_min_completed_missions", v)}
                    placeholder="10"
                    mono
                    testid="set-gold-min-missions"
                  />
                </div>
                <Field
                  label="Seuil SIRET obligatoire (€)"
                  value={settings.siret_required_min_amount ?? ""}
                  onChange={(v) => setField("siret_required_min_amount", v)}
                  placeholder="5000"
                  mono
                  testid="set-siret-threshold"
                />
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                  La TVA est appliquée automatiquement sur toutes les factures émises. Au-delà du seuil,
                  les artisans non vérifiés ne peuvent pas soumettre de devis.
                </div>
              </Section>

              <div className="lg:col-span-12 flex items-center justify-between pt-4">
                {settings.updated_at && (
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Dernière maj : {new Date(settings.updated_at).toLocaleString("fr-FR")}
                  </div>
                )}
                <button type="submit" disabled={savingSettings} className="btn-batisur flex items-center gap-2" data-testid="save-settings">
                  <Save className="w-4 h-4" /> {savingSettings ? "Enregistrement…" : "Enregistrer les paramètres"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Demandeur detail modal */}
      {openedDemandeur && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setOpenedDemandeur(null)}
          data-testid="demandeur-detail-modal"
        >
          <div
            className="bg-white border-2 border-[#0A0A0B] w-full max-w-5xl my-8 shadow-[8px_8px_0_#FF4D00]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#0A0A0B] text-white px-6 py-4 flex items-start justify-between">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Demandeur</div>
                <div className="display text-2xl mt-1">{openedDemandeur.profile.name || "—"}</div>
                <div className="text-xs text-zinc-400 mt-1">{openedDemandeur.profile.email}</div>
              </div>
              <button
                onClick={() => setOpenedDemandeur(null)}
                className="text-white hover:text-[#FF4D00]"
                data-testid="demandeur-detail-close"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 grid md:grid-cols-4 gap-3 border-b-2 border-[#0A0A0B] bg-zinc-50">
              <Mini label="Type" value={DEMANDEUR_TYPE_LABEL[openedDemandeur.profile.demandeur_type] || "Particulier"} />
              <Mini label="Ville" value={openedDemandeur.profile.city || "—"} />
              <Mini label="Téléphone" value={openedDemandeur.profile.phone || "—"} mono />
              <Mini label="Inscription" value={(openedDemandeur.profile.created_at || "").slice(0, 10) || "—"} mono />
              {openedDemandeur.profile.company && <Mini label="Entreprise" value={openedDemandeur.profile.company} />}
              {openedDemandeur.profile.siret && <Mini label="SIRET" value={openedDemandeur.profile.siret} mono />}
              <Mini label="RGPD" value={openedDemandeur.profile.terms_accepted_at ? "Consenti" : "Non tracé"} />
              <Mini label="Total dépensé" value={fmtEUR(openedDemandeur.stats.total_spent)} />
            </div>

            {/* Missions */}
            <div className="p-6">
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Missions ({openedDemandeur.stats.missions_count})</div>
              {openedDemandeur.missions.length === 0 ? (
                <div className="mt-3 text-sm text-zinc-500 italic">Aucune mission enregistrée pour ce demandeur.</div>
              ) : (
                <div className="mt-3 border-2 border-[#0A0A0B] overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Titre</th>
                        <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Catégorie</th>
                        <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Artisan</th>
                        <th className="text-right px-3 py-2 mono text-[10px] uppercase tracking-widest">Montant</th>
                        <th className="text-center px-3 py-2 mono text-[10px] uppercase tracking-widest">Statut</th>
                        <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Créée le</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openedDemandeur.missions.map((m) => (
                        <tr key={m.id} className="border-t border-zinc-200">
                          <td className="px-3 py-2 font-medium">{m.title}</td>
                          <td className="px-3 py-2 text-xs">{m.category}</td>
                          <td className="px-3 py-2 text-xs">{m.selected_artisan_name || "—"}{m.selected_artisan_company ? ` · ${m.selected_artisan_company}` : ""}</td>
                          <td className="px-3 py-2 text-right">{m.accepted_amount ? fmtEUR(m.accepted_amount) : `${fmtEUR(m.budget_min)}—${fmtEUR(m.budget_max)}`}</td>
                          <td className="px-3 py-2 text-center"><StatusBadge status={m.status} /></td>
                          <td className="px-3 py-2 text-xs text-zinc-500">{(m.created_at || "").slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Chantiers */}
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] mt-8">// Chantiers ({openedDemandeur.stats.chantiers_count})</div>
              {openedDemandeur.chantiers.length === 0 ? (
                <div className="mt-3 text-sm text-zinc-500 italic">Aucun chantier multi-lot enregistré.</div>
              ) : (
                <div className="mt-3 border-2 border-[#0A0A0B] overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Titre</th>
                        <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Ville</th>
                        <th className="text-center px-3 py-2 mono text-[10px] uppercase tracking-widest">Lots</th>
                        <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Créé le</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openedDemandeur.chantiers.map((c) => (
                        <tr key={c.id} className="border-t border-zinc-200">
                          <td className="px-3 py-2 font-medium">{c.title || "—"}</td>
                          <td className="px-3 py-2 text-xs">{c.city || "—"}</td>
                          <td className="px-3 py-2 text-center">{(c.lots || []).length}</td>
                          <td className="px-3 py-2 text-xs text-zinc-500">{(c.created_at || "").slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => openContact(openedDemandeur.profile)}
                  className="btn-batisur flex items-center gap-2"
                  data-testid="demandeur-detail-contact"
                >
                  <Mail className="w-4 h-4" /> Contacter ce demandeur
                </button>
                <button
                  onClick={() => setOpenedDemandeur(null)}
                  className="btn-anthracite"
                  data-testid="demandeur-detail-close-btn"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dispute proposal modal */}
      {proposalDispute && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !proposalSending && setProposalDispute(null)}
          data-testid="proposal-modal"
        >
          <div
            className="bg-white border-2 border-[#0A0A0B] w-full max-w-2xl my-8 shadow-[8px_8px_0_#FF4D00]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#FF4D00] text-white px-6 py-4 flex items-start justify-between">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-white/80">// Médiation Batisur</div>
                <div className="display text-xl mt-1">Proposition d'accord amiable</div>
                <div className="text-xs text-white/90 mt-1">Mission : {proposalDispute.mission?.title} · {fmtEUR(proposalDispute.amount_escrow)}</div>
              </div>
              <button onClick={() => !proposalSending && setProposalDispute(null)} className="text-white hover:text-[#0A0A0B]" data-testid="proposal-modal-close">
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Modèles pré-remplis</div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => applyProposalTemplate("partial")}
                    className="border-2 border-[#0A0A0B] p-2 text-left hover:bg-[#0A0A0B] hover:text-white transition-colors"
                    data-testid="template-partial"
                  >
                    <div className="mono text-[9px] uppercase tracking-widest text-[#FF4D00]">30% / 70%</div>
                    <div className="text-xs font-semibold mt-1">Reprise partielle</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">Travail majoritairement bon</div>
                  </button>
                  <button
                    onClick={() => applyProposalTemplate("compromise")}
                    className="border-2 border-[#0A0A0B] p-2 text-left hover:bg-[#0A0A0B] hover:text-white transition-colors"
                    data-testid="template-compromise"
                  >
                    <div className="mono text-[9px] uppercase tracking-widest text-[#FF4D00]">50% / 50%</div>
                    <div className="text-xs font-semibold mt-1">Compromis</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">Torts partagés</div>
                  </button>
                  <button
                    onClick={() => applyProposalTemplate("full_refund")}
                    className="border-2 border-[#0A0A0B] p-2 text-left hover:bg-[#0A0A0B] hover:text-white transition-colors"
                    data-testid="template-full-refund"
                  >
                    <div className="mono text-[9px] uppercase tracking-widest text-[#FF4D00]">100% / 0%</div>
                    <div className="text-xs font-semibold mt-1">Remboursement total</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">Prestation non délivrée</div>
                  </button>
                </div>
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-500">Répartition proposée</label>
                <div className="mt-2 grid grid-cols-2 gap-0 border-2 border-[#0A0A0B]">
                  <div className="p-3 border-r-2 border-[#0A0A0B] bg-zinc-50">
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Remboursé au demandeur</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={proposalForm.demandeur_refund_pct}
                        onChange={(e) => setProposalForm((f) => ({ ...f, demandeur_refund_pct: Math.min(100, Math.max(0, Number(e.target.value))) }))}
                        className="w-20 display text-3xl border border-[#0A0A0B] px-2 py-1 outline-none focus:border-[#FF4D00]"
                        data-testid="proposal-refund-input"
                      />
                      <span className="display text-2xl">%</span>
                    </div>
                    <div className="text-xs text-zinc-600 mt-1">
                      Soit <strong>{fmtEUR((proposalDispute.amount_escrow || 0) * proposalForm.demandeur_refund_pct / 100)}</strong>
                    </div>
                  </div>
                  <div className="p-3 bg-[#0A0A0B] text-white">
                    <div className="mono text-[10px] uppercase tracking-widest text-white/70">Versé à l'artisan</div>
                    <div className="display text-3xl text-[#FF4D00] mt-1">{(100 - proposalForm.demandeur_refund_pct).toFixed(0)}%</div>
                    <div className="text-xs text-white/80 mt-1">
                      Soit <strong>{fmtEUR((proposalDispute.amount_escrow || 0) * (100 - proposalForm.demandeur_refund_pct) / 100)}</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-500">Justification de la médiation *</label>
                <textarea
                  value={proposalForm.proposal}
                  onChange={(e) => setProposalForm((f) => ({ ...f, proposal: e.target.value }))}
                  placeholder="Après analyse du journal photo et des échanges, nous constatons que… Nous proposons donc de…"
                  rows={7}
                  className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 outline-none focus:border-[#FF4D00] resize-none text-sm"
                  data-testid="proposal-text-input"
                />
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                  {proposalForm.proposal.length} car. · Envoyé aux 2 parties par email
                </div>
              </div>
              <div className="border-l-4 border-amber-500 pl-3 py-2 bg-amber-50 text-xs text-amber-900">
                Les deux parties devront <strong>accepter individuellement</strong> depuis leur espace pour que les fonds soient répartis. À défaut d'accord dans les 30 jours, les fonds restent bloqués jusqu'à décision de justice (CGU 6.2).
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => !proposalSending && setProposalDispute(null)}
                  disabled={proposalSending}
                  className="btn-anthracite"
                  data-testid="proposal-cancel"
                >Annuler</button>
                <button
                  onClick={sendProposal}
                  disabled={proposalSending}
                  className="btn-batisur flex items-center gap-2"
                  data-testid="proposal-send"
                >
                  <Send className="w-4 h-4" /> {proposalSending ? "Envoi…" : "Envoyer aux deux parties"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact modal */}
      {contactTarget && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
          onClick={() => !contactSending && setContactTarget(null)}
          data-testid="contact-modal"
        >
          <div
            className="bg-white border-2 border-[#0A0A0B] w-full max-w-xl shadow-[8px_8px_0_#FF4D00]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#FF4D00] text-white px-6 py-4 flex items-start justify-between">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-white/80">// Contact direct</div>
                <div className="display text-xl mt-1">Envoyer un email</div>
              </div>
              <button onClick={() => !contactSending && setContactTarget(null)} className="text-white hover:text-[#0A0A0B]" data-testid="contact-modal-close">
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="border-2 border-[#0A0A0B] p-3 bg-zinc-50 text-sm">
                <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">À :</span>{" "}
                <strong>{contactTarget.name}</strong> · <span className="text-zinc-600">{contactTarget.email}</span>
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-500">Sujet</label>
                <input
                  type="text"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder="Ex : Suivi de votre demande sur Batisur"
                  className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 outline-none focus:border-[#FF4D00]"
                  data-testid="contact-subject-input"
                />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-500">Message</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm((f) => ({ ...f, message: e.target.value }))}
                  placeholder="Bonjour, nous vous contactons au sujet de…"
                  rows={7}
                  className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 outline-none focus:border-[#FF4D00] resize-none"
                  data-testid="contact-message-input"
                />
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                  {contactForm.message.length} car. · Envoyé depuis contact@batisur-connect.com
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => !contactSending && setContactTarget(null)}
                  disabled={contactSending}
                  className="btn-anthracite"
                  data-testid="contact-cancel"
                >Annuler</button>
                <button
                  onClick={sendContact}
                  disabled={contactSending}
                  className="btn-batisur flex items-center gap-2"
                  data-testid="contact-send"
                >
                  <Send className="w-4 h-4" /> {contactSending ? "Envoi…" : "Envoyer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent, small }) {
  return (
    <div className={`p-${small ? "5" : "6"} border-r-2 last:border-r-0 border-[#0A0A0B] ${accent ? "bg-[#FF4D00] text-white" : ""}`}>
      <Icon className={`w-${small ? "5" : "6"} h-${small ? "5" : "6"} ${accent ? "text-white" : "text-[#FF4D00]"}`} />
      <div className={`mono text-[10px] uppercase tracking-widest mt-3 ${accent ? "text-white/80" : "text-zinc-500"}`}>{label}</div>
      <div className={`display ${small ? "text-2xl" : "text-3xl"} mt-1`}>{value}</div>
      {sub && <div className={`mono text-[10px] uppercase tracking-widest mt-1 ${accent ? "text-white/70" : "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}

function Table({ cols, rows }) {
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-100">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left mono text-[11px] uppercase tracking-widest p-3 border-b-2 border-[#0A0A0B]">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} className="p-6 text-center text-zinc-500">Aucune entrée</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-zinc-50 border-b border-zinc-200" data-testid={`admin-row-${i}`}>
              {r.map((c, j) => (
                <td key={j} className="p-3 align-middle">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ icon: Icon, title, colSpan, highlight, children }) {
  return (
    <div className={`lg:col-span-${colSpan || 6} border-2 ${highlight ? "border-[#FF4D00]" : "border-[#0A0A0B]"} p-5 bg-white space-y-3`}>
      <div className={`flex items-center gap-2 mono text-[11px] uppercase tracking-widest ${highlight ? "text-[#FF4D00]" : "text-zinc-500"}`}>
        <Icon className="w-3.5 h-3.5" /> // {title}
      </div>
      <div className="display text-xl">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, mono, testid }) {
  return (
    <div>
      <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">{label}</label>
      <input
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full mt-1.5 border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF4D00] ${mono ? "mono" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}

function Mini({ label, value, mono }) {
  return (
    <div className="border-l-2 border-zinc-200 pl-3">
      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "mono" : ""} break-all`}>{value}</div>
    </div>
  );
}


function ProfileNudgeTool() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (force) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/admin/send-profile-nudges${force ? "?force=true" : ""}`);
      setResult(data);
      toast.success(`✅ ${data.sent} relance(s) envoyée(s), ${data.skipped} artisan(s) ignoré(s)`);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-2 border-[#0A0A0B] bg-white p-6 mt-6" data-testid="profile-nudge-tool">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Relance profils artisans</div>
          <div className="display text-2xl mt-1">Relancer les profils incomplets</div>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Envoie à chaque artisan un email listant <strong>chaque détail manquant</strong> (SIRET, RIB, décennale, RC Pro,
            spécialités, ville, téléphone, bio) + son <strong>lien personnel d&apos;invitation client</strong>.
            Automatique chaque semaine tant que le profil est incomplet — ce bouton force l&apos;envoi immédiat.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => run(true)}
            disabled={busy}
            className="btn-batisur text-xs"
            data-testid="profile-nudge-run-btn"
          >
            {busy ? "Envoi en cours…" : "Relancer maintenant"}
          </button>
        </div>
      </div>
      {result && (
        <div className="mt-4 text-sm" data-testid="profile-nudge-result">
          <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
            {result.sent} envoyé(s) · {result.skipped} ignoré(s) (profil complet ou relancé récemment)
          </div>
          <div className="mt-2 space-y-1 max-h-48 overflow-auto">
            {(result.details || []).map((d) => (
              <div key={d.email} className="text-xs text-zinc-700">
                <strong>{d.email}</strong> — {d.missing_count} manquant(s) : {d.missing.map((m) => m.split(" (")[0]).join(", ")}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MissionInvitationsBackfillTool() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/admin/backfill-mission-invitations${dry ? "?dry_run=true" : ""}`);
      if (dry) {
        setPreview(data);
        setResult(null);
      } else {
        setResult(data);
        setPreview(null);
        toast.success(`✅ ${data.emails_sent} email(s) + ${data.notifs_created} notif(s) envoyés`);
      }
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const data = result || preview;

  return (
    <div className="border-2 border-[#0A0A0B] bg-white p-6" data-testid="backfill-invitations-tool">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Outil rattrapage invitations</div>
          <div className="display text-2xl mt-1">Ré-envoyer les invitations manquées</div>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Ce bouton rattrape toutes les missions publiées avec un artisan invité qui n&apos;a jamais reçu son email
            ni sa notification. L&apos;opération est <strong>idempotente</strong> — vous pouvez la relancer sans risque de doublon.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => run(true)}
            disabled={busy}
            className="mono text-[10px] uppercase tracking-widest border-2 border-[#0A0A0B] px-4 py-2 hover:bg-zinc-100"
            data-testid="backfill-dry-run-btn"
          >
            {busy ? "..." : "Aperçu (dry-run)"}
          </button>
          <button
            onClick={() => run(false)}
            disabled={busy}
            className="btn-batisur text-xs"
            data-testid="backfill-run-btn"
          >
            {busy ? "Envoi en cours…" : "Lancer le rattrapage"}
          </button>
        </div>
      </div>

      {data && (
        <div className="mt-5 grid md:grid-cols-2 gap-6">
          <div className="border border-zinc-200 p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              {result ? "Résultat de l'exécution" : "Aperçu (aucun envoi effectué)"}
            </div>
            <BackfillRow label="Missions scannées" value={data.missions_scanned} />
            <BackfillRow label="Invitations traitées" value={data.invitations_processed} />
            <BackfillRow label="Emails envoyés" value={data.emails_sent} highlight={data.emails_sent > 0} />
            <BackfillRow label="Emails déjà envoyés (skip)" value={data.emails_skipped_already} />
            <BackfillRow label="Notifs créées" value={data.notifs_created} highlight={data.notifs_created > 0} />
            <BackfillRow label="Notifs déjà présentes (skip)" value={data.notifs_skipped_already} />
            <BackfillRow label="Artisans introuvables" value={data.artisans_not_found} />
          </div>
          <div className="border border-zinc-200 p-4 bg-zinc-50">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Erreurs éventuelles</div>
            {(!data.errors || data.errors.length === 0) ? (
              <div className="text-sm text-emerald-700">✅ Aucune erreur</div>
            ) : (
              <ul className="text-xs mono text-red-700 space-y-1">
                {data.errors.map((e, i) => <li key={i}>· {e}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BackfillRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-zinc-100 last:border-b-0">
      <span className="mono text-[11px] uppercase tracking-widest text-zinc-600">{label}</span>
      <span className={`mono text-sm ${highlight ? "text-[#FF4D00] font-semibold" : "text-zinc-900"}`}>{value}</span>
    </div>
  );
}
